<template>
  <div class="row gutter-sm">
    <div
      v-if="fields.includes('type')"
      class="col-xs-12"
    >
      Type
      <q-option-group
        v-model="filters.type"
        class="data-table-input"
        :options="typeOptions"
        label="Types"
        type="checkbox"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('is_showami')"
      class="col-xs-12 d-flex justify-content-between align-items-center"
    >
      <span>🈶 Only Showami</span>
      <q-toggle
        v-model="filters.is_showami"
        placeholder="🈶Is Showami"
        class="data-table-input"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes(statusFieldName)"
      class="col-xs-12"
    >
      Status
      <q-option-group
        v-model="filters[statusFieldName]"
        class="data-table-input"
        :options="statusOptions"
        label="Status"
        type="checkbox"
        @input="updateValue"
      />
    </div>
    <date-filters
      v-if="fields.includes('date')"
      :from="filters.from_date"
      :to="filters.to_date"
      @change="(datesObj)=>updateDateValue('from_date', 'to_date', datesObj)"
    />
    <date-filters
      v-if="fields.includes('tour_at_date')"
      label="Tour dates"
      :from="filters.from_tour_date"
      :to="filters.to_tour_date"
      @change="(datesObj)=>updateDateValue('from_tour_date', 'to_tour_date', datesObj)"
    />
    <date-filters
      v-if="fields.includes('schedule_date')"
      label="Schedule dates"
      :from="filters.schedule_from"
      :to="filters.schedule_to"
      @change="(datesObj)=>updateDateValue('schedule_from', 'schedule_to', datesObj)"
    />
    <date-filters
      v-if="fields.includes('last_app_usage_date')"
      label="Last app usage dates"
      :from="filters.last_app_usage_from"
      :to="filters.last_app_usage_to"
      @change="(datesObj)=>updateDateValue('last_app_usage_from', 'last_app_usage_to', datesObj)"
    />
    <div
      v-if="fields.includes('cities')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.cities"
        :options="cityOptions"
        filter
        stack-label="City"
        multiple
        class="data-table-input"
        placeholder="All cities"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('showing_type')"
      class="col-xs-12"
    >
      <q-select
        v-model="showingTypeValue"
        :options="showingTypeOptions"
        stack-label="Showing Type"
        placeholder="Chose a showing type"
        class="data-table-input"
        @input="updateShowingTypeValue"
      />
    </div>
    <div
      v-if="fields.includes('agents')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteAgent"
        placeholder="Agents"
        data-key="full_name"
        @change="autocompleteChange('agents', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('payer')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteBusiness"
        data-key="name"
        placeholder="Paying Business"
        @change="autocompleteChange('payer', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('receiver')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteAgent"
        data-key="full_name"
        placeholder="Receiving Agent"
        @change="autocompleteChange('receiver', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('tours')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteTourById"
        data-key="id"
        placeholder="Tour IDs"
        @change="autocompleteChange('tours', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('properties')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteProperty"
        data-key="short_address"
        placeholder="Properties"
        @change="autocompleteChange('properties', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('businesses')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteBusiness"
        data-key="name"
        placeholder="Businesses"
        @change="autocompleteChange('businesses', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('markets')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.markets"
        :options="marketOptions"
        filter
        stack-label="Markets"
        multiple
        class="data-table-input"
        placeholder="All markets"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('subscription_plans')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.subscription_plans"
        :options="subscriptionPlanOptions"
        stack-label="Subscription plan"
        multiple
        class="data-table-input"
        placeholder="All subscription plans"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('schedule_id')"
      class="col-xs-12"
    >
      <q-input
        v-model="filters.scheduleId"
        type="number"
        placeholder="Schedule ID"
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('inspections')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteInspectionById"
        data-key="id"
        placeholder="Inspection IDs"
        @change="autocompleteChange('inspections', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('leads')"
      class="col-xs-12"
    >
      <autocomplete-chips
        method="autocompleteLead"
        data-key="full_name"
        placeholder="Leads"
        @change="autocompleteChange('leads', ...arguments)"
      />
    </div>
    <div
      v-if="fields.includes('is_vacant')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.is_vacant"
        :options="vacancyStatuses"
        filter
        stack-label="Vacancy status"
        class="data-table-input"
        placeholder="All vacancy statuses"
        clearable
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('is_inspections_capable')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.is_inspections_capable"
        :options="inspectionCapabilityOptions"
        filter
        stack-label="Inspection capability"
        class="data-table-input"
        placeholder="All inspection capabilities"
        clearable
        @input="updateValue"
      />
    </div>
    <div
      v-if="fields.includes('provider')"
      class="col-xs-12"
    >
      <q-select
        v-model="filters.provider"
        :options="deviceProviderOptions"
        filter
        stack-label="Device Provider"
        class="data-table-input"
        placeholder="All Device Providers"
        clearable
        @input="updateValue"
      />
    </div>
    <div class="col-xs-12">
      <q-btn
        rounded
        outline
        color="primary"
        label="Reset"
        class="full-width"
        @click="reset"
      />
    </div>
  </div>
</template>

<script>
import { clone } from 'quasar';
import { ShowingTypes } from '@/constants/ShowingProfile';
import { providers } from '@/constants/AccessHardware';
import AutocompleteChips from '@/components/autocomplete-chips.vue';
import DateFilters from './date-filter';

export default {
  name: 'BjFilters',
  components: { AutocompleteChips, DateFilters },
  props: {
    value: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    statuses: {
      type: [Object, Array],
      required: false,
    },
    showingTypes: {
      type: [Object, Array],
      required: false,
    },
    statusFieldName: {
      type: String,
      default: 'status',
    },
    types: {
      type: Object,
      required: false,
    },
    cities: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      filters: this.value,
      statusOptions: this.statuses && prepareOptions(this.statuses),
      showingTypeOptions: this.fields.includes('showing_type') && prepareOptions(getShowingTypeOptions()),
      typeOptions: this.types && prepareOptions(this.types),
      cityOptions: this.cities && prepareOptions(this.cities),
      showingTypeValue: defaultShowingTypeOptionValue,
      marketOptions: [],
      subscriptionPlanOptions: [
        { label: 'Basic', value: 0 },
        { label: 'Plus', value: 1 },
      ],
      deviceProviderOptions: Object.values(providers),
      vacancyStatuses: [
        {
          label: 'Vacant',
          value: 1,
        },
        {
          label: 'Occupied',
          value: 0,
        },
      ],
      inspectionCapabilityOptions: [
        {
          label: 'Inspection capable',
          value: '1',
        },
        {
          label: 'Inspection incapable',
          value: '0',
        },
      ],
    };
  },
  created() {
    if (this.fields.includes('markets')) {
      this.getMarkets().then((markets) => {
        this.marketOptions = markets.map((market) => ({
          value: market.id,
          label: market.name,
        }));
      });
    }
  },
  methods: {
    reset() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = Array.isArray(this.filters[key]) ? [] : null;
      });
      this.updateValue();
      this.$emit('reset');
    },
    updateValue() {
      this.$emit('input', this.filters);
    },
    updateShowingTypeValue() {
      this.filters.showingType = this.showingTypeValue === defaultShowingTypeOptionValue
        ? null
        : this.showingTypeValue;
      this.updateValue();
    },
    updateDateValue(fromKey, toKey, changedDatesObj) {
      this.filters[fromKey] = changedDatesObj.from;
      this.filters[toKey] = changedDatesObj.to;
      this.updateValue();
    },
    autocompleteChange(field, items, navigateWithKeyboardArrows) {
      const filters = { ...this.filters };
      if (!navigateWithKeyboardArrows) {
        if (!items) {
          filters[field] = undefined;
        } else {
          filters[field] = items.map((item) => item.data.id);
        }
        this.filters = filters;
        this.updateValue();
      }
    },
  },
};
const defaultShowingTypeOptionValue = 'all';
function getShowingTypeOptions() {
  const options = {
    ALL: defaultShowingTypeOptionValue,
    ...ShowingTypes,
  };

  delete options.PUBLISH_LISTING;

  return options;
}
function prepareOptions(items) {
  // already a formatted options
  if (Array.isArray(items)) {
    return items;
  }

  return Object.keys(items).map((key) => {
    const item = clone(items[key]);
    let label = item.split('_').join(' ').split('.').join(' ');
    label = label.charAt(0).toUpperCase() + label.slice(1);

    return {
      value: item,
      label,
    };
  });
}
</script>
